/**
 * Routing pedonale tempo-dipendente che minimizza
 *   costo = lunghezza × (1 + α · esposizione_al_sole)
 *
 * L'esposizione di ogni arco dipende dall'orario stimato in cui lo si
 * percorre (bucket di 15 minuti): camminando, il sole si sposta.
 * α = 0 dà il percorso più breve; α alto compra ombra pagando distanza.
 */

import type { Bbox, LatLng } from './geo';
import { bboxOf, distXY, expandBbox, haversineMeters } from './geo';
import type { Graph, GraphEdge } from './graph';
import { cloneGraphForRouting, insertPointOnEdge, snapToGraph } from './graph';
import type { ShadeEngine } from './shadow';
import { sunPosition, type SunPosition } from './sun';

export const WALK_SPEED_MPS = 1.33; // ~4.8 km/h
export const STEPS_SPEED_FACTOR = 0.6;
export const BUCKET_MS = 15 * 60_000;
/** valori di α provati per costruire il fronte di Pareto in modalità automatica */
export const AUTO_ALPHAS = [0.6, 1.2, 2, 3.2, 5, 8];
export const MAX_SNAP_DIST_M = 250;
/** vincolo della modalità automatica: allungamento massimo accettato */
const AUTO_MAX_EXTRA_PCT = 60;
/** peso della distanza extra nello score della modalità automatica */
const AUTO_LENGTH_WEIGHT = 0.55;
/** guadagno minimo di ombra (punti %) per preferire una deviazione */
const AUTO_MIN_GAIN_PP = 2;

export const MAX_BEELINE_M = 6000;

export class RoutingError extends Error {
  constructor(
    message: string,
    readonly userMessage: string,
  ) {
    super(message);
    this.name = 'RoutingError';
  }
}

/**
 * Bbox dell'area da scaricare per una richiesta A→B: inviluppo dei due punti
 * con un buffer proporzionale alla distanza (min 300 m, max 800 m).
 */
export function areaBboxFor(a: LatLng, b: LatLng): Bbox {
  const beeline = haversineMeters(a, b);
  if (beeline > MAX_BEELINE_M) {
    throw new RoutingError(
      `distanza in linea d'aria ${Math.round(beeline)} m oltre il limite`,
      `I due punti distano ${(beeline / 1000).toFixed(1)} km in linea d'aria: per ora il limite è ${MAX_BEELINE_M / 1000} km (a piedi è già una bella camminata!). Avvicina i punti.`,
    );
  }
  const buffer = Math.max(300, Math.min(800, beeline * 0.25));
  return expandBbox(bboxOf([a, b]), buffer);
}

/** Cache dell'ombra per arco e bucket temporale, condivisa tra più run. */
export class ShadeCache {
  private readonly byBucket = new Map<number, Float32Array>();
  private readonly sunByBucket = new Map<number, SunPosition>();

  constructor(
    private readonly engine: ShadeEngine,
    private readonly baseEdgeCount: number,
    private readonly center: LatLng,
  ) {}

  sunAt(tMs: number): SunPosition {
    const bucket = Math.floor(tMs / BUCKET_MS);
    let sun = this.sunByBucket.get(bucket);
    if (!sun) {
      sun = sunPosition((bucket + 0.5) * BUCKET_MS, this.center.lat, this.center.lng);
      this.sunByBucket.set(bucket, sun);
    }
    return sun;
  }

  shadeOf(edge: GraphEdge, tMs: number): number {
    const sun = this.sunAt(tMs);
    if (edge.baseIdx < 0) {
      return this.engine.shadeOfPolyline(edge.ptsXY, sun, edge.covered);
    }
    const bucket = Math.floor(tMs / BUCKET_MS);
    let arr = this.byBucket.get(bucket);
    if (!arr) {
      arr = new Float32Array(this.baseEdgeCount).fill(NaN);
      this.byBucket.set(bucket, arr);
    }
    let v = arr[edge.baseIdx];
    if (Number.isNaN(v)) {
      v = this.engine.shadeOfPolyline(edge.ptsXY, sun, edge.covered);
      arr[edge.baseIdx] = v;
    }
    return v;
  }
}

class MinHeap {
  private keys: number[] = [];
  private vals: number[] = [];

  get size(): number {
    return this.keys.length;
  }

  push(key: number, val: number): void {
    const k = this.keys;
    const v = this.vals;
    k.push(key);
    v.push(val);
    let i = k.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (k[parent] <= k[i]) break;
      [k[parent], k[i]] = [k[i], k[parent]];
      [v[parent], v[i]] = [v[i], v[parent]];
      i = parent;
    }
  }

  pop(): [number, number] | null {
    const k = this.keys;
    const v = this.vals;
    if (k.length === 0) return null;
    const top: [number, number] = [k[0], v[0]];
    const lastK = k.pop() as number;
    const lastV = v.pop() as number;
    if (k.length > 0) {
      k[0] = lastK;
      v[0] = lastV;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < k.length && k[l] < k[m]) m = l;
        if (r < k.length && k[r] < k[m]) m = r;
        if (m === i) break;
        [k[m], k[i]] = [k[i], k[m]];
        [v[m], v[i]] = [v[i], v[m]];
        i = m;
      }
    }
    return top;
  }
}

export interface RouteSegment {
  coords: LatLng[];
  lengthM: number;
  /** ombra media 0..1 del tratto all'orario stimato di percorrenza */
  shade: number;
  name?: string;
  isSteps: boolean;
  /** galleria/portico: ombra piena a qualunque ora */
  covered: boolean;
}

export interface RouteResult {
  alpha: number;
  coords: LatLng[];
  lengthM: number;
  durationS: number;
  /** frazione di percorso in ombra, pesata sulla lunghezza (0..1) */
  shadeFrac: number;
  segments: RouteSegment[];
  /** vertici attraversati (indici nel grafo della richiesta): serve all'export minimale */
  vertexPath: number[];
}

function traversalTimeS(edge: GraphEdge, speedMps: number): number {
  return edge.lengthM / (speedMps * (edge.isSteps ? STEPS_SPEED_FACTOR : 1));
}

/** A* tempo-dipendente. Ritorna null se la destinazione non è raggiungibile. */
export function findRoute(
  g: Graph,
  cache: ShadeCache,
  startV: number,
  endV: number,
  alpha: number,
  departMs: number,
  speedMps = WALK_SPEED_MPS,
): RouteResult | null {
  const n = g.vertices.length;
  const dist = new Float64Array(n).fill(Infinity);
  const timeAt = new Float64Array(n).fill(Infinity);
  const prevEdge = new Int32Array(n).fill(-1);
  const settled = new Uint8Array(n);
  const end = g.vertices[endV];

  const heap = new MinHeap();
  dist[startV] = 0;
  timeAt[startV] = departMs;
  heap.push(distXY(g.vertices[startV], end), startV);

  while (heap.size > 0) {
    const [, u] = heap.pop() as [number, number];
    if (settled[u]) continue;
    settled[u] = 1;
    if (u === endV) break;

    for (const ei of g.adj[u]) {
      const edge = g.edges[ei];
      const v = edge.a === u ? edge.b : edge.a;
      if (settled[v]) continue;
      const shade = cache.shadeOf(edge, timeAt[u]);
      const w = edge.lengthM * (1 + alpha * (1 - shade));
      const nd = dist[u] + w;
      if (nd < dist[v] - 1e-9) {
        dist[v] = nd;
        timeAt[v] = timeAt[u] + traversalTimeS(edge, speedMps) * 1000;
        prevEdge[v] = ei;
        heap.push(nd + distXY(g.vertices[v], end), v);
      }
    }
  }

  if (!Number.isFinite(dist[endV])) return null;

  // ricostruzione all'indietro
  const edgeIdxs: number[] = [];
  let v = endV;
  while (v !== startV) {
    const ei = prevEdge[v];
    if (ei < 0) return null;
    edgeIdxs.push(ei);
    const edge = g.edges[ei];
    v = edge.a === v ? edge.b : edge.a;
  }
  edgeIdxs.reverse();

  const segments: RouteSegment[] = [];
  const coords: LatLng[] = [];
  let lengthM = 0;
  let durationS = 0;
  let shadeWeighted = 0;
  let cursor = startV;
  let tMs = departMs;
  const vertexPath: number[] = [startV];

  for (const ei of edgeIdxs) {
    const edge = g.edges[ei];
    const forward = edge.a === cursor;
    const segCoords = forward ? edge.coords : [...edge.coords].reverse();
    const shade = cache.shadeOf(edge, tMs);
    segments.push({
      coords: segCoords,
      lengthM: edge.lengthM,
      shade,
      name: edge.name,
      isSteps: edge.isSteps,
      covered: edge.covered,
    });
    for (let i = coords.length > 0 ? 1 : 0; i < segCoords.length; i++) coords.push(segCoords[i]);
    lengthM += edge.lengthM;
    const dt = traversalTimeS(edge, speedMps);
    durationS += dt;
    tMs += dt * 1000;
    shadeWeighted += shade * edge.lengthM;
    cursor = forward ? edge.b : edge.a;
    vertexPath.push(cursor);
  }

  return {
    alpha,
    coords,
    lengthM,
    durationS,
    shadeFrac: lengthM > 0 ? shadeWeighted / lengthM : 0,
    segments,
    vertexPath,
  };
}

/**
 * Waypoint minimi per l'export verso Google Maps.
 *
 * Google non accetta un tracciato arbitrario: fra due punti segue il SUO
 * percorso piu' breve, e l'unica leva sono i waypoint (che Maps mostra come
 * tappe). Invece di spargerli lungo tutto il tracciato, ne mettiamo uno solo
 * dove il percorso all'ombra DIVERGE dal piu' breve: il nostro shortest fa da
 * proxy del routing di Google. Greedy: da ogni waypoint si cerca (ricerca
 * binaria: i prefissi di un cammino minimo sono cammini minimi) il punto piu'
 * lontano del percorso fino al quale il piu' breve coincide col percorso
 * stesso; li' va il prossimo waypoint. Su un percorso che coincide col piu'
 * breve il risultato e' zero waypoint.
 */
export function minimalWaypoints(
  g: Graph,
  cache: ShadeCache,
  route: RouteResult,
  departMs: number,
  speedMps = WALK_SPEED_MPS,
  maxWaypoints = 9,
): LatLng[] {
  const path = route.vertexPath;
  if (path.length < 3) return [];

  const sameAsShortest = (i: number, j: number): boolean => {
    const sp = findRoute(g, cache, path[i], path[j], 0, departMs, speedMps);
    if (!sp) return false;
    const sub = path.slice(i, j + 1);
    if (sp.vertexPath.length !== sub.length) return false;
    for (let k = 0; k < sub.length; k++) if (sp.vertexPath[k] !== sub[k]) return false;
    return true;
  };

  const waypoints: LatLng[] = [];
  let i = 0;
  const last = path.length - 1;
  while (i < last) {
    if (sameAsShortest(i, last)) break; // da qui in avanti Google seguirebbe gia' il nostro percorso
    let lo = i + 1;
    let hi = last - 1;
    let best = i + 1; // un singolo arco coincide sempre (salvo archi paralleli)
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (sameAsShortest(i, mid)) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    waypoints.push(g.verticesLL[path[best]]);
    i = best;
    if (waypoints.length >= maxWaypoints) return []; // troppi: il chiamante usa il fallback geometrico
  }
  return waypoints;
}

export interface ParetoPoint {
  alpha: number;
  lengthM: number;
  durationS: number;
  shadeFrac: number;
  /** allungamento % rispetto al più breve */
  extraLengthPct: number;
  /** guadagno di ombra in punti percentuali rispetto al più breve */
  shadeGainPp: number;
  chosen: boolean;
}

export interface PlanRequest {
  startLL: LatLng;
  endLL: LatLng;
  departMs: number;
  /** 'auto' = scelta consigliata sul fronte di Pareto; altrimenti α esplicito */
  mode: 'auto' | { alpha: number };
  speedMps?: number;
}

export interface PlanResult {
  chosen: RouteResult;
  shortest: RouteResult;
  pareto: ParetoPoint[];
  night: boolean;
  sunAtDepart: SunPosition;
  snappedStart: LatLng;
  snappedEnd: LatLng;
  /**
   * Waypoint intermedi minimi per «Apri in Google Maps»: vuoto se il percorso
   * scelto coincide col piu' breve (Google lo seguirebbe da solo) o se ne
   * servirebbero troppi (in quel caso l'export usa il fallback geometrico).
   */
  exportWaypoints: LatLng[];
  /** true se exportWaypoints e' vuoto perche' ne servivano piu' del massimo */
  exportNeedsFallback: boolean;
}

/**
 * Pipeline completa su un grafo base già costruito: snap di A e B,
 * percorso più breve, ventaglio di α e scelta del percorso.
 */
export function planRoutes(baseGraph: Graph, cache: ShadeCache, req: PlanRequest): PlanResult {
  const g = cloneGraphForRouting(baseGraph);

  const snapA = snapToGraph(g, req.startLL, MAX_SNAP_DIST_M);
  if (!snapA) {
    throw new RoutingError(
      'partenza troppo lontana dal grafo',
      'Il punto di partenza è troppo lontano da strade percorribili a piedi (max 250 m). Spostalo più vicino a una via.',
    );
  }
  const startV = insertPointOnEdge(g, snapA.edgeIdx, snapA.t);
  const snapB = snapToGraph(g, req.endLL, MAX_SNAP_DIST_M);
  if (!snapB) {
    throw new RoutingError(
      'arrivo troppo lontano dal grafo',
      'Il punto di arrivo è troppo lontano da strade percorribili a piedi (max 250 m). Spostalo più vicino a una via.',
    );
  }
  const endV = insertPointOnEdge(g, snapB.edgeIdx, snapB.t);

  if (startV === endV) {
    throw new RoutingError(
      'partenza e arrivo coincidono',
      'Partenza e arrivo coincidono (o quasi): scegli due punti più distanti.',
    );
  }

  const speed = req.speedMps ?? WALK_SPEED_MPS;
  const shortest = findRoute(g, cache, startV, endV, 0, req.departMs, speed);
  if (!shortest) {
    throw new RoutingError(
      'nessun percorso trovato',
      'Non esiste un collegamento pedonale tra i due punti nell’area scaricata. Prova con punti più vicini.',
    );
  }

  const sunAtDepart = cache.sunAt(req.departMs);
  const night = sunAtDepart.elevationDeg <= 0.3;

  const alphas = req.mode === 'auto' ? AUTO_ALPHAS : [req.mode.alpha];
  const candidates: RouteResult[] = [shortest];
  if (!night) {
    for (const alpha of alphas) {
      if (alpha <= 0) continue;
      const r = findRoute(g, cache, startV, endV, alpha, req.departMs, speed);
      if (r) candidates.push(r);
    }
  }

  // dedup per lunghezza+ombra (α diversi spesso danno lo stesso percorso)
  const unique: RouteResult[] = [];
  for (const r of candidates) {
    if (
      !unique.some(
        (u) => Math.abs(u.lengthM - r.lengthM) < 0.5 && Math.abs(u.shadeFrac - r.shadeFrac) < 0.002,
      )
    ) {
      unique.push(r);
    }
  }

  let chosen: RouteResult;
  if (night) {
    chosen = shortest;
  } else if (req.mode === 'auto') {
    chosen = shortest;
    let bestScore = AUTO_MIN_GAIN_PP;
    for (const r of unique) {
      const extraPct = (r.lengthM / shortest.lengthM - 1) * 100;
      if (extraPct > AUTO_MAX_EXTRA_PCT) continue;
      const gainPp = (r.shadeFrac - shortest.shadeFrac) * 100;
      const score = gainPp - AUTO_LENGTH_WEIGHT * extraPct;
      if (score > bestScore) {
        bestScore = score;
        chosen = r;
      }
    }
  } else {
    // modalità manuale: l'ultimo candidato è il run con l'α richiesto
    chosen = candidates[candidates.length - 1];
  }

  const pareto: ParetoPoint[] = unique
    .map((r) => ({
      alpha: r.alpha,
      lengthM: r.lengthM,
      durationS: r.durationS,
      shadeFrac: r.shadeFrac,
      extraLengthPct: (r.lengthM / shortest.lengthM - 1) * 100,
      shadeGainPp: (r.shadeFrac - shortest.shadeFrac) * 100,
      chosen: r === chosen,
    }))
    .sort((a, b) => a.lengthM - b.lengthM);

  const chosenIsShortest = Math.abs(chosen.lengthM - shortest.lengthM) < 0.5;
  const exportWaypoints = chosenIsShortest
    ? []
    : minimalWaypoints(g, cache, chosen, req.departMs, speed);
  const exportNeedsFallback = !chosenIsShortest && exportWaypoints.length === 0;

  return {
    chosen,
    shortest,
    pareto,
    night,
    sunAtDepart,
    snappedStart: g.verticesLL[startV],
    snappedEnd: g.verticesLL[endV],
    exportWaypoints,
    exportNeedsFallback,
  };
}

/** Converte il valore dello slider (0..100) nell'α del costo. */
export function alphaFromSlider(p: number): number {
  const clamped = Math.max(0, Math.min(100, p));
  return Math.pow(clamped / 100, 1.6) * 8;
}
